/**
 * Auto Suggest
 * An auto suggest container.
 *
 * @example
 * new AutoSuggest(el, {
 *   // Optional. Callback for when the input value changes.
 *   onChange(value, inputInstance) {}
 * });
 *
 * @module components/auto-suggest.js
 */
import BaseComponent from './base';
import toggleClass from '../helpers/dom/toggle-class';
import hasClass from '../helpers/dom/has-class';
import {number as parseNumberAttribute, boolean as parseBooleanAttribute} from '../helpers/dom/parse-attribute';
import debounce from '../helpers/util/debounce';
import hasParent from '../helpers/traversal/has-parent';
import getParent from '../helpers/traversal/get-parent';
import messaging from '../mixins/messaging';
import mixin from '../helpers/util/mixin';
import makeElement from '../helpers/dom/make-element';

const noop = function() {};

class AutoSuggest extends BaseComponent {

  /**
   * AutoSuggest constructor.
   * @param {Element} el
   * @param {Object} params
   */
  constructor(el, params = {}) {

    params.elRequired = true;

    if (!super(el, params)) {
      return;
    }

    this._bindEventListenerCallbacks();
    this._addEventListeners();

    if (this.inputEl.value) {
      this.show();
      this._onInput();
    }
  }


  /**
   * Cache elements.
   * @param {Element} el
   */
  _cacheElements(el) {

    this.el = el;
    this.inputEl = this.el.querySelector('.spark-auto-suggest__field');
    this.labelEl = this.el.querySelector('.spark-label');
    this.listBoxEl = this.el.querySelector('.spark-auto-suggest__listbox');
    this.clearButtonEl = this.el.querySelector('.spark-auto-suggest__clear-btn');

    if (!this.inputEl) {
      throw new Error('No <input> element present in input container!', this.el);
    }

    this.messageEl = this.el.querySelector('.spark-input__message') || makeElement('<span class="spark-input__message"></span>');
  }


  /**
   * Parse parameters from the elements.
   */
  _parseParams() {

    this.clickedWithinAutoSuggest = false;
    this.isActive = this.inputEl.value ? true : false;
    this.autoSuggestTerms = this.autoSuggestTerms !== null && Array.isArray(this.autoSuggestTerms) ? this.autoSuggestTerms : null;
    this.minLength = this.minLength !== null ? this.minLength : parseNumberAttribute(this.el, 'data-min-length', 2);
    this.maxSuggestions = this.maxSuggestions !== null ? this.maxSuggestions : parseNumberAttribute(this.el, 'data-max-suggestions', 2);
    this.showAllTerms = this.showAllTerms !== null ? this.showAllTerms : parseBooleanAttribute(this.el, 'data-show-all-terms', false);
    this.showNoResultsFound = this.showNoResultsFound !== null ? this.showNoResultsFound : parseBooleanAttribute(this.el, 'data-show-no-results-found', false);
    this.noResultsFoundText = this.noResultsFoundText !== null ? this.noResultsFoundText : 'No results found';

    if (this.autoSuggestTerms) {
      this.termsObject = typeof this.autoSuggestTerms[0] === 'object' ? true : false;
    }

    if (this.autoSuggestTerms === null && this.customGetSuggestions === null) {
      throw new Error('No Auto Suggest terms or custom search function provided!', this.el);
    }
  }


  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because the
   * function signatures won't match.
   */
  _bindEventListenerCallbacks() {

    this._onFocusBound = this._onFocus.bind(this);
    this._onBlurBound = debounce(this._onBlur.bind(this), 100);
    this._onInputBound = this._onInput.bind(this);
    this._onClearClickBound = this._onClearClick.bind(this);
    this._onWindowClickBound = this._onWindowClick.bind(this);
    this._onKeyupBound = this._onKeyup.bind(this);
    this._onKeydownBound = this._onKeydown.bind(this);
  }


  /**
   * Add event listeners for DOM events.
   */
  _addEventListeners() {

    this.inputEl.addEventListener('focus', this._onFocusBound);
    this.inputEl.addEventListener('blur', this._onBlurBound);
    this.inputEl.addEventListener('input', this._onInputBound);

    if (this.clearButtonEl) {
      this.clearButtonEl.addEventListener('click', this._onClearClickBound);
    }

    this._addWindowEventListeners();
  }


  /**
   * Remove event listeners for DOM events..
   */
  _removeEventListeners() {

    this.inputEl.removeEventListener('focus', this._onFocusBound);
    this.inputEl.removeEventListener('blur', this._onBlurBound);
    this.inputEl.removeEventListener('input', this._onInputBound);

    if (this.clearButtonEl) {
      this.clearButtonEl.removeEventListener('click', this._onClearClickBound);
    }

    this._removeWindowEventListeners();
  }


  /**
   * Add event listeners for DOM events.
   */
  _addWindowEventListeners() {

    this._removeWindowEventListeners();
    window.addEventListener('click', this._onWindowClickBound);
    window.addEventListener('keyup', this._onKeyupBound);
    window.addEventListener('keydown', this._onKeydownBound);
  }


  /**
   * Remove event listeners for DOM events.
   */
  _removeWindowEventListeners() {

    window.removeEventListener('click', this._onWindowClickBound);
    window.removeEventListener('keyup', this._onKeyupBound);
    window.removeEventListener('keydown', this._onKeydownBound);
  }


  /**
   * When the input element gains focus.
   * @param {Object} e
   */
  _onFocus() {

    this.show();
    toggleClass(this.inputEl, 'focus', true);

    // Hide the Clear button if it is visible but there is no entry in the field
    // Also adjust padding on input depending on presence and visibility of the Clear Button
    if (this.clearButtonEl && this.inputEl.value === '') {
      toggleClass(this.clearButtonEl, 'visible', false);
      toggleClass(this.inputEl, 'clear-btn-visible', false);
    }

    // Show all terms on initial focus if configured and there isn't an initial entry
    if (this.showAllTerms && !this.listBoxElOpen && !this.inputEl.value && this.customGetSuggestions === null) {
      this._displayAllSuggestions();
      return;
    }

    // If input has a value, display the list if there are matches
    if (this.inputEl.value && !this.listBoxElOpen) {
      const inputString = this.inputEl.value;

      if ( inputString.length > 0 ) {
        if (this.clearButtonEl) {
          toggleClass(this.clearButtonEl, 'visible', true);
          toggleClass(this.inputEl, 'clear-btn-visible', true);
        }
      }

      if ( inputString.length >= this.minLength ) {
        this._updateSuggestionsDisplay();
      }
    }
  }


  /**
   * When the input element loses focus.
   * @param {Object} e
   */
  _onBlur() {

    if (this.clickedWithinAutoSuggest) {
      this.clickedWithinAutoSuggest = false;
      this.inputEl.focus();
      return;
    }

    const activeElement = document.activeElement;

    if (!hasParent(activeElement, this.el)) {
      if (!this.inputEl.value) {
        this.hide();
      }

      this._closeListbox();
      toggleClass(this.inputEl, 'focus', false);
    }
  }


  /**
   * When the input receives a value or the value is about to change, update the displayed suggestions
   * @param {Object} e
   */
  _onInput() {

    this.listBoxEl.innerHTML = '';

    toggleClass(this.listBoxEl, 'visible', false);

    const inputString = this.inputEl.value;

    // If a value was set using value="" but focus is not on the input, do not show suggestions
    // However, show the Clear button since a value is indeed present
    if (document.activeElement !== this.inputEl && inputString.length > 0) {
      if (this.clearButtonEl) {
        toggleClass(this.clearButtonEl, 'visible', true);
        toggleClass(this.inputEl, 'clear-btn-visible', true);
      }
      return;
    }

    this.showingAllTerms = (inputString.length === 0 && this.showAllTerms) ? true : false;

    if (this.showingAllTerms) {
      this._displayAllSuggestions();
    }

    if (inputString.length > 0 && this.clearButtonEl) {
      toggleClass(this.clearButtonEl, 'visible', true);
      toggleClass(this.inputEl, 'clear-btn-visible', true);
    } else {
      toggleClass(this.clearButtonEl, 'visible', false);
      toggleClass(this.inputEl, 'clear-btn-visible', false);
    }

    if ( inputString.length >= this.minLength ) {
      this._updateSuggestionsDisplay();
    }

    (this.onChange || noop)(this.inputEl.value, this);
  }


  /**
   * Create the HTML markup of a suggestion term
   * @param {String} text The suggestion value in HTML format
   * @param {Number} index The index of the suggestion value
   */
  _createSuggestionMarkup(term, index) {
    if (this.termsObject) {

      // Add icon if available
      if (this.autoSuggestTerms[index].icon && this.autoSuggestTerms[index].icon !== '') {
        term = this.autoSuggestTerms[index].icon + '<span class="spark-auto-suggest__list-item__value" data-term-index="' + index + '">' + term + '</span>';
      } else {
        term = '<span class="spark-auto-suggest__list-item__value" data-term-index="' + index + '">' + term + '</span>';
      }

      // Add description if available
      if (this.autoSuggestTerms[index].description && this.autoSuggestTerms[index].description !== '') {
        term += '<span class="spark-auto-suggest__list-item__description">' + this.autoSuggestTerms[index].description + '</span>';
      }
    }

    return term;
  }


  /**
   * Create the HTML markup of a list item
   * @param {String} text The suggestion string in HTML format, including icon and description markup if set
   * @param {Number} index The index of the list item
   */
  _createSuggestionsListItemMarkup(htmlStr, index) {
    let item = document.createElement('li');
    item.setAttribute('role', 'option');
    item.setAttribute('id', 'spark-auto-suggest__list-item--' + index);
    item.setAttribute('class', 'spark-auto-suggest__list-item');
    item.setAttribute('tabindex', '-1');

    if (this.termsObject) {
      item.setAttribute('data-suggestion-object', true);
    }

    item.innerHTML = htmlStr;

    return item;
  }


  /**
   * Display all Auto Suggest terms. Should not be used in conjunction with showNoResultsFound
   */
  _displayAllSuggestions() {
    if ( this.autoSuggestTerms.length < 1 ) {
      return;
    }

    this.showingAllTerms = true;

    let suggestionResults = [];

    for (let i = 0; i < this.autoSuggestTerms.length; i++) {
      let suggestion = this.termsObject ? this.autoSuggestTerms[i].value : this.autoSuggestTerms[i];
      suggestion = this._createSuggestionMarkup(suggestion, i);
      suggestionResults.push(suggestion);
    }

    if (suggestionResults.length) {
      for (let i = 0; i < suggestionResults.length; i++) {
        let resultItem = this._createSuggestionsListItemMarkup(suggestionResults[i], i);
        this.listBoxEl.appendChild(resultItem);
      }

      this._openListbox();
      this.resultsCount = suggestionResults.length;
    }
  }

  /**
   * Get and display Auto Suggest terms based on user entry
   */
  _updateSuggestionsDisplay() {

    let suggestionResults = [];

    // If custom filtering and display functionality is provided, call it and exit
    if (this.customGetSuggestions) {
      (this.customGetSuggestions.bind(this))();
      return;
    }

    for (let i = 0; i < this.autoSuggestTerms.length; i++) {
      let term = this.termsObject ? this.autoSuggestTerms[i].value : this.autoSuggestTerms[i];

      if (term.toLowerCase().indexOf(this.inputEl.value.toLowerCase()) !== -1) {
        let regex = new RegExp(this.inputEl.value, 'gi');
        let suggestion = term.replace(regex, function(string) {
          return '<mark>' + string + '</mark>';
        });

        suggestion = this._createSuggestionMarkup(suggestion, i);

        suggestionResults.push(suggestion);
      }

      if (suggestionResults.length === this.maxSuggestions) {
        break;
      }
    }

    if (suggestionResults.length) {

      // Ensure that listbox is empty before appending content. IE adds "null" to empty <ul> elements
      this.listBoxEl.innerHTML = '';

      for (let i = 0; i < suggestionResults.length; i++) {
        let resultItem = this._createSuggestionsListItemMarkup(suggestionResults[i], i);
        this.listBoxEl.appendChild(resultItem);
      }

      this._openListbox();
      this.resultsCount = suggestionResults.length;
    } else {
      this.resultsCount = 0;

      if (this.showNoResultsFound) {
        let noResult = document.createElement('li');
        noResult.setAttribute('role', 'presentation');
        noResult.setAttribute('class', 'spark-auto-suggest__list-item spark-auto-suggest__list-item--no-results');
        noResult.innerHTML = this.noResultsFoundText;
        this.listBoxEl.appendChild(noResult);

        this._openListbox();
      }
    }
  }


  /**
   * When the Clear Button is clicked, clear the entered value and close the listbox if it is open
   * If showAllTerms is true, display all the terms once the field is clear
   * @param {Object} e
   */
  _onClearClick(e) {

    e.preventDefault();

    this.inputEl.value = '';
    this.oldVal = null;
    this.inputEl.focus();
    this._closeListbox();

    this.clickedWithinAutoSuggest = false;

    if (this.clearButtonEl) {
      toggleClass(this.clearButtonEl, 'visible', false);
      toggleClass(this.inputEl, 'clear-btn-visible', false);
    }

    // If showAllTerms is true we need to show the full list on click
    if (this.showAllTerms && !this.listBoxElOpen && !this.inputEl.value && this.customGetSuggestions === null) {
      this._displayAllSuggestions();
    }

    // Manually call onChange method with newly empty input
    (this.onChange || noop)(this.inputEl.value, this);
  }


  /**
   * Show the input by adding the active state
   */
  _updateClass() {
    toggleClass(this.el, 'active', this.isActive);
  }


  /**
   * When the window is clicked and the area clicked is not part of the Auto Suggest, close the suggestions list
   * If an item in the suggestions list is clicked, set it as the active/selected item
   * @param {Object} e
   */
  _onWindowClick(e) {

    // Check if we have clicked outside of the Auto Suggest component and collapse the Suggestions list if so
    if (e.target !== this.el && !hasParent(e.target, this.el) && e.target !== this.listBoxEl && !hasParent(e.target, this.listBoxEl)) {
      this._closeListbox();
      toggleClass(this.inputEl, 'focus', false);
      return;
    }

    // If we clicked the label, input field or clear button, exit
    if (e.target === this.inputEl || e.target === this.labelEl || e.target === this.clearButtonEl) {
      return;
    }

    // If a valid suggestion wasn't clicked, exit
    if (e.target.getAttribute('role') !== 'option' && !hasParent(e.target, this.listBoxEl)) {
      this.clickedWithinAutoSuggest = true;
      return;
    }

    // Set the selected suggestion as the input's value
    let targetID;

    if (e.target.tagName !== 'li') {
      let li = getParent(e.target, 'li', this.listBoxEl);
      targetID = li.getAttribute('id');
    } else {
      targetID = e.target.getAttribute('id');
    }

    this._setActiveSuggestion(targetID);

    // Set focus back to input but dismiss listbox
    this.inputEl.focus();

    // Ensure that _closeListbox is triggered after focus above in IE
    setTimeout(function() {
      this._closeListbox();
    }.bind(this), 0);

    // Allow additional behavior on mouse click of a suggestion
    (this.onSelection || noop)(this.inputEl.value, this);
  }


  /**
   * Set the selected item
   */
  _setActiveSuggestion(id) {

    let activeSuggestion = document.getElementById(id);

    if (activeSuggestion) {
      if (this.termsObject) {
        const valueEl = activeSuggestion.querySelector('.spark-auto-suggest__list-item__value');
        this.inputEl.value = valueEl.innerText;
        this.selectedTermsObjectIndex = valueEl.getAttribute('data-term-index');
      } else {
        this.inputEl.value = activeSuggestion.innerText;
        this.oldVal = this.inputEl.value;
      }

      // Show the Clear button if an entry has been selected via keyboard arrows or direct mouseclick
      if (this.clearButtonEl && this.inputEl.value !== '') {
        toggleClass(this.clearButtonEl, 'visible', true);
        toggleClass(this.inputEl, 'clear-btn-visible', true);
      }
    }
  }


  /**
   * When a key is pressed on the window and it's an ESC, close the suggestions list
   * @param {Object} e
   */
  _onKeyup(e) {
    if (e.keyCode === 27 && document.activeElement === this.inputEl) {
      this._closeListbox();
      this.inputEl.value = '';

      if (this.oldVal !== this.inputEl.value) (this.onChange || noop)(this.inputEl.value, this);
    }

    if (e.keyCode !== 38 && e.keyCode !== 40) {
      this.oldVal = this.inputEl.value;
    }
  }


  /**
   * When a key is pressed and it is the Enter key, set the current suggestion as the selected one.
   * If it is the tab key, determine where to shift focus and whether to manage the component's display classes
   * If it is the arrow keys, navigate the suggestions list
   * @param {Object} e
   */
  _onKeydown(e) {

    // Remove focus from component if navigating away from it via Tab key
    if (e.keyCode === 9 && document.activeElement === this.clearButtonEl) {
      this._closeListbox();
      toggleClass(this.inputEl, 'focus', false);
    }

    if (this.resultsCount < 1) {
      return;
    }

    // Set focus back to input if printable keys are pressed
    if (e.keyCode !== 13 && e.keyCode !== 38 && e.keyCode !== 40 && document.activeElement !== this.inputEl) {
      this.inputEl.focus();
      return;
    }

    // Enter key
    if (e.keyCode === 13) {
      e.preventDefault();

      if (document.activeElement !== this.inputEl) {
        this.inputEl.focus();

        // Ensure that _closeListbox is triggered after focus above in IE
        setTimeout(function() {
          this._closeListbox();
        }.bind(this), 0);

        // Allow form submission or other behavior on Enter
        // @deprecated Remove onEnterKey in v4.0.0
        (this.onSelection || this.onEnterKey || noop)(this.inputEl.value, this);
      }

      return;
    }

    // Get the current suggestion which will become "previous" suggestion
    const currentSuggestion = this.getAutoSuggestItem(this.activeSuggestionIndex);

    // Up arrow
    if (e.keyCode === 38) {
      e.preventDefault();

      if (document.activeElement === this.inputEl) {
        this.activeSuggestionIndex = this.resultsCount - 1;
      } else if (this.activeSuggestionIndex <= 0) {
        this.activeSuggestionIndex = -1;

        // Reset value back to what was initially typed
        this.inputEl.value = this.oldVal;
        this.inputEl.setAttribute('aria-activedescendant', '');
        this.inputEl.focus();
      } else {
        this.activeSuggestionIndex--;
      }

      // Down arrow
    } else if (e.keyCode === 40) {
      e.preventDefault();

      if (document.activeElement === this.inputEl) {
        this.activeSuggestionIndex = 0;
      } else if (this.activeSuggestionIndex >= this.resultsCount - 1) {
        this.activeSuggestionIndex = -1;

        // Reset value back to what was initially typed
        this.inputEl.value = this.oldVal;
        this.inputEl.setAttribute('aria-activedescendant', '');
        this.inputEl.focus();
      } else {
        this.activeSuggestionIndex++;
      }
    }

    // Exit at this point if not Up/Down arrow keys
    if (e.keyCode !== 38 && e.keyCode !== 40) {
      return;
    }

    // Find the item at the activeSuggestionIndex
    let newSuggestion;

    if (this.activeSuggestionIndex >= 0) {
      newSuggestion = this.getAutoSuggestItem(this.activeSuggestionIndex);
    }

    if (currentSuggestion) {

      // Remove focus from single action variation if it exists
      let link = currentSuggestion.querySelector('.spark-link');
      if (link) link.blur();

      currentSuggestion.setAttribute('aria-selected', 'false');
      toggleClass(currentSuggestion, 'focus', false);
    }

    if (newSuggestion) {

      // If this is the single action variation, we need to ignore it
      if ( hasClass(newSuggestion, 'spark-auto-suggest__list-item--action') ) {
        let link = newSuggestion.querySelector('.spark-link');
        if (link) link.focus();

        // Reset value of input to initially typed item
        this.inputEl.value = this.oldVal;
        this.inputEl.setAttribute('aria-activedescendant', '');

        return;
      }

      this.inputEl.setAttribute('aria-activedescendant', 'spark-auto-suggest__list-item--' + this.activeSuggestionIndex);
      toggleClass(newSuggestion, 'focus', true);
      newSuggestion.setAttribute('aria-selected', 'true');
      newSuggestion.focus();

      // Make this new selection the value of the text input but store old value for cycling purposes
      const targetID = 'spark-auto-suggest__list-item--' + this.activeSuggestionIndex;
      this._setActiveSuggestion(targetID);
    }
  }


  /**
   * Display the suggestions list
   */
  _openListbox() {
    toggleClass(this.listBoxEl, 'visible', true);
    this.showingAllTerms === true ? toggleClass(this.listBoxEl, 'show-all-terms', true) : toggleClass(this.listBoxEl, 'show-all-terms', false);
    this.listBoxElOpen = true;
    this.el.setAttribute('aria-expanded', 'true');
  }


  /**
   * Hide the suggestions list
   */
  _closeListbox() {

    if (!this.listBoxElOpen) {
      return;
    }

    // Manage visibility of suggestions list, and associated ARIA attributes
    toggleClass(this.listBoxEl, 'visible', false);
    toggleClass(this.listBoxEl, 'show-all-terms', false);
    this.listBoxElOpen = false;
    this.listBoxEl.innerHTML = '';

    this.el.setAttribute('aria-expanded', 'false');
    this.inputEl.setAttribute('aria-activedescendant', '');

    // Reset whether we are showing all terms
    this.showingAllTerms = false;

    // Reset index and results counter
    this.activeSuggestionIndex = -1;
    this.resultsCount = 0;
  }


  /**
  * Get a listbox element
  * @param {Number} index The index of the element to retrieve
  */
  getAutoSuggestItem(index) {
    return document.getElementById('spark-auto-suggest__list-item--' + index);
  }


  /**
   * Show the input by adding the active state
   */
  show() {
    this.isActive = true;
    this._updateClass();
    return this;
  }


  /**
   * Hide the input by removing the active state.
   */
  hide() {
    this.isActive = false;
    this._updateClass();
    return this;
  }


  /**
   * Set the value of the input
   * @param {Mixed} value
   */
  setValue(value) {
    this.inputEl.value = value;

    if (value) {
      this.show();
      toggleClass(this.clearButtonEl, 'visible', true);
      toggleClass(this.inputEl, 'clear-btn-visible', true);
    } else {
      this.hide();
    }

    return this;
  }


  /**
   * Get the value of the input.
   * @return {String}
   */
  getValue() {
    return this.inputEl.value;
  }


  /**
   * Clear the value of the input.
   */
  clearValue() {
    this.inputEl.value = '';
    return this;
  }


  /**
   * Disable the Auto Suggest.
   */
  disable() {
    this._closeListbox();
    this.inputEl.blur();
    this.inputEl.setAttribute('disabled', true);

    if (this.clearButtonEl) {
      this.clearButtonEl.setAttribute('disabled', true);
    }

    return this;
  }


  /**
   * Enable the Auto Suggest.
   */
  enable() {
    this.inputEl.removeAttribute('disabled');

    if (this.clearButtonEl) {
      this.clearButtonEl.removeAttribute('disabled');
    }

    return this;
  }


  /**
   * Remove component from DOM.
   * @param {Boolean} leaveElement
   */
  remove(leaveElement) {
    return super.remove(leaveElement);
  }


  /**
   * Update the element in use and the position.
   * @param {Element} el
   */
  update(el) {
    return super.update(el);
  }
}


/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
AutoSuggest.prototype._whitelistedParams = [
  'onChange',
  'onEnterKey',
  'onSelection',
  'autoSuggestTerms',
  'showAllTerms',
  'showNoResultsFound',
  'minLength',
  'maxSuggestions',
  'noResultsFoundText',
  'customGetSuggestions'
];

/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
AutoSuggest.prototype.defaults = {
  el: null,
  inputEl: null,
  labelEl: null,
  clearButtonEl: null,
  listBoxEl: null,
  isActive: false,
  clickedWithinAutoSuggest: false,
  oldVal: null,
  showingAllTerms: false,
  resultsCount: 0,
  activeSuggestionIndex: -1,
  termsObject: null,
  selectedTermsObjectIndex: null,
  listBoxElOpen: false,
  minLength: null,
  maxSuggestions: 7,
  autoSuggestTerms: null,
  showNoResultsFound: null,
  showAllTerms: null,
  noResultsFoundText: null,
  customGetSuggestions: null,
  onChange: null,
  onEnterKey: null,
  onSelection: null,
  _onFocusBound: null,
  _onBlurBound: null,
  _onInputBound: null,
  _onClearClickBound: null,
  _onKeyupBound: null,
  _onKeydownBound: null,
  _onWindowClickBound: null
};

mixin(AutoSuggest.prototype, messaging);

export default AutoSuggest;
